'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm, useWatch } from 'react-hook-form'
import JobApplicationHeader from '../JobApplicationHeader'
import JobApplicationFields from '../JobApplicationFields'
import JobApplicationNotice from '../JobApplicationNotice'
import { useSubmitJobApplicationMutation } from '@/generated/graphql'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { useTurnstile } from '@/Turnstile'

function formatJobTitle(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

type JobApplicationFormData = {
  name: string
  email: string
  location: string
  resume: FileList | null
  additionalNotes: string
}

const JobApplicationForm = ({ jobSlug }: JobApplicationFormProps) => {
  const router = useRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<JobApplicationFormData>({
    defaultValues: {
      name: '',
      email: '',
      location: '',
      resume: null,
      additionalNotes: '',
    },
  })

  const { mutate: submitJobApplication } = useSubmitJobApplicationMutation()
  const { executeTurnstile } = useTurnstile()

  const resumeFile = useWatch({ control, name: 'resume' })?.[0]

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        resolve(result)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const getErrorMessage = (error: unknown): string => {
    const errorObj = error as { message?: string }
    const message = errorObj?.message?.toLowerCase() || ''

    // Filter out technical error details
    if (
      message.includes('inner:') ||
      message.includes('stacktrace') ||
      message.includes('exception') ||
      message.includes('failed to send')
    ) {
      return 'There was an issue submitting your application. Please try again. If the problem persists, please email us at magic@vanishingincmagic.com'
    }

    // Return a generic user-friendly message
    return 'An error occurred submitting your application. Please try again.'
  }

  const onSubmit = async (data: JobApplicationFormData) => {
    setSubmitError(null)
    setIsSubmitting(true)

    try {
      // Validate file type
      const allowedExtensions =
        /(\.doc|\.docx|\.odt|\.pdf|\.txt|\.rtf|\.pages)$/i
      if (resumeFile && !allowedExtensions.exec(resumeFile.name)) {
        setSubmitError(
          'Unsupported file type. Please use PDF, DOC, DOCX, ODT, TXT, RTF, or Pages format.'
        )
        setIsSubmitting(false)
        return
      }

      // Validate file size (4MB limit)
      if (resumeFile && resumeFile.size > 4 * 1024 * 1024) {
        setSubmitError('Resume file size must be under 4MB.')
        setIsSubmitting(false)
        return
      }

      // Execute Turnstile verification
      const token = await executeTurnstile()
      const captchaResponse = await fetch('/api/verifycaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })

      if (!captchaResponse.ok) {
        setSubmitError(
          'Unable to verify you are human. Please try again or email us at magic@vanishingincmagic.com'
        )
        setIsSubmitting(false)
        return
      }

      // Convert CV to base64
      const cvBase64 = resumeFile ? await convertFileToBase64(resumeFile) : null
      const jobTitle = formatJobTitle(jobSlug)

      submitJobApplication(
        {
          name: data.name,
          email: data.email,
          location: data.location || '',
          jobTitle: jobTitle,
          jobSlug: jobSlug,
          additionalNotes: data.additionalNotes || null,
          cvBase64: cvBase64,
          cvFileName: resumeFile?.name || null,
        },
        {
          onSuccess: () => {
            router.push(
              `/jobs/thank-you?jobTitle=${encodeURIComponent(jobTitle)}&email=${encodeURIComponent(data.email)}`
            )
          },
          onError: (error: unknown) => {
            setSubmitError(getErrorMessage(error))
            setIsSubmitting(false)
          },
        }
      )
    } catch (err) {
      setSubmitError(getErrorMessage(err))
      setIsSubmitting(false)
    }
  }

  const allErrors = [
    ...(Object.values(errors)
      .map(error => error?.message)
      .filter(Boolean) as string[]),
    ...(submitError ? [submitError] : []),
  ]

  return (
    <div className="max-w-2xl py-2 px-4">
      <JobApplicationHeader jobSlug={jobSlug} />

      <ErrorBox errors={allErrors} />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <JobApplicationFields
          register={register}
          errors={errors}
          resumeFile={resumeFile}
        />
        <JobApplicationNotice />
        <div className="pt-4">
                    <button
            type="submit"
            className="btn btn-success"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default JobApplicationForm

interface JobApplicationFormProps {
  jobSlug: string
}
